import { observer } from 'mobx-react'
import { useContext } from 'react'
import type { ReactNode } from 'react'
import { Button, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { cx } from '@/utils/cx'
import type { InfoScreen as InfoScreenType } from '@/types/dataCollection'
import { ScreenHeader } from './ScreenHeader'
import { resolveLabel } from './helpers'
import { HelpSection } from './HelpSection'

interface InfoScreenProps {
  screen: InfoScreenType
  /** Rendered under the screen header, e.g. the retry notice. */
  notice?: ReactNode
  submitting: boolean
  canGoBack: boolean
  onBack: () => void
  onSubmit: () => void
}

export const InfoScreen = observer(
  ({ screen, notice, submitting, canGoBack, onBack, onSubmit }: InfoScreenProps) => {
    const store = useContext(StoreContext)
    const { language } = store.TranslationsState
    const { isMobile } = store.AppState
    const { dataCollectionPage: trans } = store.TranslationsState.translations

    return (
      <div className="page-container mobile-m-0">
        <div className="layout__content-container-with-scroll">
          <ScreenHeader title={screen.title} description={screen.description} icon={screen.icon} />
          {notice}

          {(screen.sections ?? []).map((section, index) => (
            <div className="data-collection__section" key={index}>
              {section.title && (
                <span className="data-collection__section-title">
                  {resolveLabel(section.title, language)}
                </span>
              )}
              {section.items.map((item, i) => (
                <div className="data-collection__message" key={i}>
                  <span className="data-collection__message-number">{i + 1}</span>
                  <span className="data-collection__message-text">
                    {resolveLabel(item, language)}
                  </span>
                </div>
              ))}
            </div>
          ))}

          {screen.help && <HelpSection help={screen.help} />}
        </div>

        <div className="layout__content-container-bottom-fixed">
          {canGoBack ? (
            <Button
              onClick={onBack}
              disabled={submitting}
              className="button--secondary data-collection__back"
              dataTestid="data-collection-back"
            >
              {trans.back}
            </Button>
          ) : (
            !isMobile && <div className="layout__content-container-bottom-left" />
          )}
          <Button onClick={onSubmit} disabled={submitting} dataTestid="data-collection-next">
            {submitting && <LoadingSpinner inButton />}
            <span
              className={cx('generic__button-text', submitting && 'generic__button-text--loading')}
            >
              {trans.next}
            </span>
          </Button>
        </div>
        <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
      </div>
    )
  }
)
